/**
 * S9 cross-tab information flow — the five tabs as one instrument.
 *
 * Canonical path (plan §S9):
 *   Today → Movement (your period) → Listen (period's sound)
 *         → Discover (proof) → Ask (only if the user initiates).
 *
 * Watch chapters ride alongside via WatchNextFooter and are reached from
 * Movement ("your period") and the session-return chip.
 */
import type { TFunction } from "i18next";

export type JourneyStepId = "movement" | "listen" | "discover" | "ask";

export interface JourneyStep {
  id: JourneyStepId;
  /** Canonical route for an active chart, or the chart-less fallback. */
  to: (chartId: string | null) => string;
  titleKey: string;
  copyKey: string;
  /**
   * Ask is offered but never pushed (law: "only if user initiates").
   * Ordered steps drive the single primary "what's next" CTA.
   */
  userInitiatedOnly?: boolean;
}

export const JOURNEY_STEPS: readonly JourneyStep[] = [
  {
    id: "movement",
    to: (chartId) => (chartId ? `/movement/${chartId}` : "/movement"),
    titleKey: "ui.web.journey.movement_title",
    copyKey: "ui.web.journey.movement_copy",
  },
  {
    id: "listen",
    // /station, not /life-music: the Astro Radio synthesises on the device.
    // ADR-017 section 4 rules the server-rendered path out of the per-listener
    // product surface; /life-music is KEPT for the broadcast channel it permits
    // (plan C3).
    to: (chartId) => (chartId ? `/station/${chartId}` : "/listen"),
    titleKey: "ui.web.journey.listen_title",
    copyKey: "ui.web.journey.listen_copy",
  },
  {
    id: "discover",
    to: () => "/discover",
    titleKey: "ui.web.journey.discover_title",
    copyKey: "ui.web.journey.discover_copy",
  },
  {
    id: "ask",
    to: () => "/ask",
    titleKey: "ui.web.journey.ask_title",
    copyKey: "ui.web.journey.ask_copy",
    userInitiatedOnly: true,
  },
];

/** Steps that participate in the ordered "what's next" path. */
export const ORDERED_STEPS: readonly JourneyStep[] = JOURNEY_STEPS.filter(
  (step) => !step.userInitiatedOnly,
);

export function stepAfter(id: JourneyStepId): JourneyStep | null {
  const index = ORDERED_STEPS.findIndex((step) => step.id === id);
  if (index < 0) return ORDERED_STEPS[0] ?? null;
  // Wrap to the head so the path never dead-ends at Discover.
  return ORDERED_STEPS[(index + 1) % ORDERED_STEPS.length] ?? null;
}

export function stepBefore(id: JourneyStepId): JourneyStep | null {
  const index = ORDERED_STEPS.findIndex((step) => step.id === id);
  if (index <= 0) return null;
  return ORDERED_STEPS[index - 1] ?? null;
}

/** The single primary CTA for a surface sitting on the journey. */
export function nextStepFrom(id: JourneyStepId | "today"): JourneyStep {
  if (id === "today") return ORDERED_STEPS[0] ?? JOURNEY_STEPS[0];
  return stepAfter(id) ?? ORDERED_STEPS[0] ?? JOURNEY_STEPS[0];
}

// ── Session return path (local only; no server session) ────────────────────

const RETURN_KEY = "astrogenesis.watch.return";

export interface WatchReturnPoint {
  chartId: string;
  chapter: string;
  label: string;
  at: number;
}

export function rememberWatchReturn(point: WatchReturnPoint): void {
  try {
    window.localStorage.setItem(RETURN_KEY, JSON.stringify(point));
  } catch {
    /* private mode / quota — return chip simply won't persist */
  }
}

export function readWatchReturn(): WatchReturnPoint | null {
  try {
    const raw = window.localStorage.getItem(RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<WatchReturnPoint>;
    if (
      typeof parsed.chartId !== "string" ||
      typeof parsed.chapter !== "string" ||
      typeof parsed.label !== "string"
    ) {
      return null;
    }
    return {
      chartId: parsed.chartId,
      chapter: parsed.chapter,
      label: parsed.label,
      at: typeof parsed.at === "number" ? parsed.at : 0,
    };
  } catch {
    return null;
  }
}

export function clearWatchReturn(): void {
  try {
    window.localStorage.removeItem(RETURN_KEY);
  } catch {
    /* ignore */
  }
}

export function watchReturnHref(point: WatchReturnPoint): string {
  return `/watch/${point.chartId}/${point.chapter}`;
}

export function formatWatchReturn(point: WatchReturnPoint, t: TFunction): string {
  return t("ui.web.journey.continue_from", { chapter: point.label });
}
